// The single source of truth for the Usage Metrics screen: every tile and overlay, which way is good for
// it, and what clicking it lists. A tile, its delta chip and its drill-down all read from here, so they
// cannot disagree.
//
// A drill is a set of search-panel fields. Clicking fills the panel with it and runs the same search the
// Search button runs, so the panel always shows why the grid is filtered, Reset clears it, and the
// query string carries it.

// The empty search panel. Field names double as the query-string keys.
export const initialSearchFields = {
  Bucket: "",
  Verdict: "",
  Flag: "",
  UserName: "",
  Prompt: "",
  Topic: "",
};

// The kinds with no tool call - the no-API-call tile's numerator, as the server counts it.
const NO_API_CALL_KINDS = ["AnsweredWithoutData", "UnsupportedRequest", "NoDataRequest"];

// Each grid row is { ...its own columns, Turn: the stored payload, Classification: the verdict }, so filters
// name the nested member. The server resolves the paths the same way the grid's dataFields do.
const TURN = "Turn.";
const CLASSIFICATION = "Classification.";

// An overlay counts only scored turns, so its drill-down leaves the excluded ones out too.
const scoredWith = (field) => [[CLASSIFICATION + field, "=", true], "and", [CLASSIFICATION + "Verdict", "<>", "Excluded"]];

const FLAG_FILTERS = {
  IdentifierFriction: scoredWith("IdentifierFriction"),
  CoverageViolation: scoredWith("CoverageViolation"),
  Repaired: scoredWith("Repaired"),
  DeliveredButDisliked: [CLASSIFICATION + "DeliveredButDisliked", "=", true],
  NoApiCall: NO_API_CALL_KINDS.reduce(
    (expr, kind) => {
      const one = [CLASSIFICATION + "Kind", "=", kind];
      return expr.length ? [...expr, "or", one] : [one];
    },
    []
  ),
  // By kind, not by Result bucket: a rating moves a turn's bucket but never its kind, and the tiles these
  // back count by kind - a Liked answered-without-data turn is in the tile but in the Delivered bucket.
  AnsweredWithoutData: [CLASSIFICATION + "Kind", "=", "AnsweredWithoutData"],
  Unrecognised: [CLASSIFICATION + "Kind", "=", "Unknown"],
  Rated: [CLASSIFICATION + "FeedbackAction", "<>", null],
};

/** The grid filter for a set of search-panel fields - null when nothing is set. */
export const buildFilters = (fields) => {
  const filters = [];
  const push = (expr) => {
    if (filters.length > 0) filters.push("and");
    filters.push(expr);
  };

  if (fields.Bucket) push(["Bucket", "=", fields.Bucket]);
  if (fields.Verdict) push([CLASSIFICATION + "Verdict", "=", fields.Verdict]);
  if (fields.Flag && FLAG_FILTERS[fields.Flag]) push(FLAG_FILTERS[fields.Flag]);
  if (fields.UserName) push(["UserName", "contains", fields.UserName]);
  if (fields.Prompt) push([TURN + "Prompt", "contains", fields.Prompt]);
  if (fields.Topic) push([CLASSIFICATION + "Topic", "=", fields.Topic]);

  return filters.length > 0 ? filters : null;
};

// kind: "rate" reads a { Numerator, Denominator, Value } rate; "ratio" reads a bare number. Keys are the
// UsageMetricsDTO property names, which the API returns PascalCase.
// higherIsBetter decides the colour of the delta chip - never inferred from the sign.
export const USAGE_TILES = [
  // Quality - over the scored turns (delivered + failures).
  {
    key: "DeliveryRate", caption: "Delivery rate", row: "quality", kind: "rate", noun: "scored",
    higherIsBetter: true,
    // The turns the tile counts, not the ones it does not. Drilling to the failures was deliberate -
    // the reasoning being that a reviewer wants to see what went wrong - but it made this the only
    // figure on the screen whose click did not list what it counted, so the row count never matched
    // the tile. The failures have their own count beside the range, and a breakdown below.
    drill: { Verdict: "Success" },
  },
  {
    key: "TurnsPerAnswer", caption: "Turns per answer", row: "quality", kind: "ratio",
    higherIsBetter: false, drill: { Verdict: "Failure" },
    tooltip: "Scored turns per delivered turn - exactly the reciprocal of the delivery rate, not independent evidence.",
  },
  {
    key: "DeliveredButDisliked", caption: "Delivered but disliked", row: "quality", kind: "rate", noun: "delivered",
    higherIsBetter: false, fromFeedback: true, drill: { Flag: "DeliveredButDisliked" },
  },
  {
    key: "AnsweredWithoutData", caption: "Answered without data", row: "quality", kind: "rate", noun: "scored",
    higherIsBetter: false, drill: { Flag: "AnsweredWithoutData" },
  },
  {
    key: "IdentifierFriction", caption: "Identifier friction", row: "quality", kind: "rate", noun: "scored",
    higherIsBetter: false, drill: { Flag: "IdentifierFriction" },
  },

  // Context - over every turn, and not quality measures.
  {
    key: "NoApiCall", caption: "No API call", row: "context", kind: "rate", noun: "all turns",
    higherIsBetter: false, drill: { Flag: "NoApiCall" },
  },
  {
    key: "FeedbackCoverage", caption: "Feedback coverage", row: "context", kind: "rate", noun: "rated",
    higherIsBetter: true, drill: { Flag: "Rated" },
  },
  {
    key: "Unrecognised", caption: "Unrecognised", row: "context", kind: "rate", noun: "all turns",
    higherIsBetter: false, drill: { Flag: "Unrecognised" },
    // The classifier's own blind spot: while it is above zero every other figure is provisional, so it
    // stops being dimmed and is made hard to ignore.
    alertWhenNonZero: true,
  },
];

// Beside the failure breakdown, never in it: each can co-occur with any kind.
export const USAGE_OVERLAYS = [
  {
    key: "CoverageViolation", caption: "Coverage violation", kind: "rate", noun: "scored", higherIsBetter: false,
    drill: { Flag: "CoverageViolation" },
  },
  {
    key: "Repaired", caption: "Repaired by the composer", kind: "rate", noun: "scored", higherIsBetter: false,
    drill: { Flag: "Repaired" },
  },
];

/** The figure a tile or overlay shows, as a number - null when there is nothing to divide by. */
export const valueOf = (metric, metrics) => {
  if (!metrics) return null;
  const raw = metrics[metric.key];
  if (metric.kind === "ratio") return raw ?? null;
  return raw && raw.Value != null ? raw.Value : null;
};

/**
 * The change since the previous period: percentage points for a rate, the plain difference for a ratio.
 * Null when either period has no value. `good` is true, false, or null for no change.
 */
export const deltaOf = (metric, current, previous) => {
  const now = valueOf(metric, current);
  const before = valueOf(metric, previous);
  if (now == null || before == null) return null;

  const change = metric.kind === "ratio" ? now - before : (now - before) * 100;
  const rounded = metric.kind === "ratio" ? Math.round(change * 10) / 10 : Math.round(change);
  const good = rounded === 0 ? null : (rounded > 0) === metric.higherIsBetter;

  return { change: rounded, good, unit: metric.kind === "ratio" ? "" : "pp" };
};
